import moment from 'moment'

/**
 * Utilidades de formateo de fechas para el front.
 *
 * Funciones puras (no dependen de Vue ni de `this.$moment`) para que sean
 * usables desde componentes Vue, desde .js no-Vue (stores, helpers de
 * testing) y desde contextos donde el plugin global no esté inyectado.
 *
 * El resto del código usa `this.$moment(value).format('DD-MM-YYYY HH:mm')`
 * (4 dígitos de año). Acá exponemos `formatDateTime` con `YY` (2 dígitos)
 * porque es el formato pedido por el feature del scanner; si en el futuro
 * se quiere normalizar todo a 4 dígitos, cambiar el formato en una línea.
 *
 * Para fechas en zona horaria específica (e.g. America/Santiago) usar
 * directamente moment-timezone en el caller; este helper usa la zona local.
 */

const EMPTY_VALUES = [null, undefined, '', 'null', 'undefined']

/**
 * Devuelve true si el valor es falsy o representa "ausente" en las
 * formas que llegan del backend (string vacío, string 'null', undefined).
 */
function isEmpty (value) {
  return value === null || value === undefined || value === '' || value === 'null'
}

/**
 * Formatea una fecha/hora al formato corto `DD-MM-YY HH:mm`.
 *
 * Ejemplos:
 *   formatDateTime('2026-07-12T15:30:00.000Z') // '12-07-26 15:30'
 *   formatDateTime(new Date())                  // '12-07-26 15:30'
 *   formatDateTime(null)                        // ''
 *   formatDateTime('valor inválido')            // '' (input crudo)
 *
 * Devuelve string vacío para entradas vacías o inválidas en vez de tirar,
 * porque el llamador típico (template Vue) no quiere un `Invalid Date`
 * colgado en la UI.
 */
export function formatDateTime (value) {
  if (isEmpty(value)) return ''
  const m = moment(value)
  if (!m.isValid()) return ''
  return m.format('DD-MM-YY HH:mm')
}

/**
 * Variante que devuelve '—' (em-dash) en lugar de string vacío, para los
 * casos donde la celda siempre debe mostrar algo visualmente. Útil cuando
 * la columna es "hora" y querés un placeholder consistente en vez de una
 * celda vacía.
 */
export function formatDateTimeOrDash (value) {
  const formatted = formatDateTime(value)
  return formatted || '—'
}
